import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Endpoints } from '../../models/Endpoints';

@Injectable({
  providedIn: 'root',
})
export class Api {
  private http = inject(HttpClient);

  /**
   * Resolves a known `Endpoints` enum value to its configured environment URL,
   * or returns the string as-is if it is already a full URL.
   * @param endpoint : Endpoints | string - An `Endpoints` enum member or a raw URL string.
   */
  public getUrl(endpoint: Endpoints | string): string {
    if (Object.values(Endpoints).includes(endpoint as Endpoints)) {
      return environment[endpoint as keyof typeof environment];
    }
    return endpoint;
  }

  /**
   * Performs an HTTP GET request.
   * @param endpointOrUrl : Endpoints | string - An `Endpoints` enum member or a full URL.
   * @param params : any - Optional query parameters.
   */
  get<T>(endpointOrUrl: Endpoints | string, params?: any): Observable<T> {
    const url = this.getUrl(endpointOrUrl);
    return this.http.get<T>(url, { params });
  }

  /**
   * Performs an HTTP POST request.
   * @param endpointOrUrl : Endpoints | string - An `Endpoints` enum member or a full URL.
   * @param body : any - The request body.
   * @param contentType : string - Optional Content-Type header value.
   */
  post<T>(endpointOrUrl: Endpoints | string, body: any, contentType?: string): Observable<T> {
    const url = this.getUrl(endpointOrUrl);
    const headers = new HttpHeaders();
    if (contentType) {
      headers.set('Content-Type', contentType);
    }
    return this.http.post<T>(url, body, { headers });
  }

  /**
   * Performs an HTTP PUT request.
   * @param endpointOrUrl : Endpoints | string - An `Endpoints` enum member or a full URL.
   * @param body : any - The request body.
   */
  put<T>(endpointOrUrl: Endpoints | string, body: any): Observable<T> {
    const url = this.getUrl(endpointOrUrl);
    return this.http.put<T>(url, body);
  }

  /**
   * Performs an HTTP PATCH request.
   * @param endpointOrUrl : Endpoints | string - An `Endpoints` enum member or a full URL.
   * @param body : any - The partial request body.
   */
  patch<T>(endpointOrUrl: Endpoints | string, body: any): Observable<T> {
    const url = this.getUrl(endpointOrUrl);
    return this.http.patch<T>(url, body);
  }
}
